import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { SparxTeaserData } from "@/lib/contentParsers/sparxTeaserSchema";

export type SparxTeaserLoadStatus = "idle" | "loading" | "loaded" | "error";

export type SparxTeaserState = {
  fileName: string | null;
  data: SparxTeaserData | null;
  status: SparxTeaserLoadStatus;
};

const initialState: SparxTeaserState = {
  fileName: null,
  data: null,
  status: "idle",
};

/** Settled teaser for a file (loaded, optional payload). Useful for Storybook/tests. */
export const createSettledSparxTeaserState = (
  fileName: string,
  data: SparxTeaserData | null = null,
): SparxTeaserState => ({
  fileName,
  data,
  status: "loaded",
});

const sparxTeaserSlice = createSlice({
  name: "sparxTeaser",
  initialState,
  reducers: {
    sparxTeaserLoadStarted: (
      _state,
      action: PayloadAction<{ fileName: string }>,
    ): SparxTeaserState => ({
      fileName: action.payload.fileName,
      data: null,
      status: "loading",
    }),
    sparxTeaserLoadSucceeded: (
      state,
      action: PayloadAction<{
        fileName: string;
        data: SparxTeaserData | null;
      }>,
    ): SparxTeaserState => {
      if (state.fileName !== action.payload.fileName) {
        return state;
      }
      return {
        fileName: action.payload.fileName,
        data: action.payload.data,
        status: "loaded",
      };
    },
    sparxTeaserLoadFailed: (
      state,
      action: PayloadAction<{ fileName: string }>,
    ): SparxTeaserState => {
      if (state.fileName !== action.payload.fileName) {
        return state;
      }
      return {
        fileName: action.payload.fileName,
        data: null,
        status: "error",
      };
    },
    clearSparxTeaser: (): SparxTeaserState => initialState,
  },
});

export const {
  sparxTeaserLoadStarted,
  sparxTeaserLoadSucceeded,
  sparxTeaserLoadFailed,
  clearSparxTeaser,
} = sparxTeaserSlice.actions;

export default sparxTeaserSlice.reducer;

/** True while loading (optimistic) or after a successful load with data for this file. */
export const selectHasSparxTeaser = (
  state: SparxTeaserState,
  activeFileName?: string,
): boolean => {
  if (
    activeFileName != null &&
    activeFileName.length > 0 &&
    state.fileName !== activeFileName
  ) {
    return false;
  }
  return (
    state.status === "loading" ||
    (state.status === "loaded" && state.data != null)
  );
};

/**
 * Whether teaser fetch finished for this insight file (success or missing/error).
 * Stale state from another file does not count as settled.
 */
export const selectIsSparxTeaserSettledForFile = (
  state: SparxTeaserState,
  fileName: string,
): boolean =>
  state.fileName === fileName &&
  (state.status === "loaded" || state.status === "error");
